// Composable for URL import state management
import { ref, computed } from 'vue'
import * as api from '~/javascript/api/onboarding'
import consumer from '~/channels/consumer'

const STEP_LABELS = {
  fetching: 'Fetching website content',
  crawling: 'Discovering additional pages',
  screenshotting: 'Taking screenshots',
  downloading_images: 'Downloading images',
  extracting: 'Extracting business information',
  analyzing: 'AI analyzing design and content',
  normalizing: 'Preparing your data'
}

export default function useImport(websiteId) {
  // State
  const importId = ref(null)
  const status = ref('idle') // idle, pending, fetching, screenshotting, extracting, analyzing, normalizing, completed, failed
  const progress = ref(0)
  const currentStep = ref(null)
  const normalizedData = ref(null)
  const error = ref(null)
  const subscription = ref(null)
  const screenshots = ref({ desktop: null, mobile: null })

  // Computed
  const isImporting = computed(() => ['pending', 'fetching', 'screenshotting', 'extracting', 'analyzing', 'normalizing'].includes(status.value))

  const isComplete = computed(() => status.value === 'completed')

  const hasFailed = computed(() => status.value === 'failed')

  const stepLabel = computed(() => STEP_LABELS[currentStep.value] || 'Processing...')

  // Actions
  async function startImport(url) {
    status.value = 'pending'
    progress.value = 0
    error.value = null
    normalizedData.value = null

    try {
      const response = await api.createImport(websiteId, url)
      importId.value = response.data.id

      // Subscribe to ActionCable for real-time updates
      subscribeToUpdates()

      return response.data
    } catch (err) {
      status.value = 'failed'
      error.value = err.response?.data?.error || 'Failed to start import'
      throw err
    }
  }

  function subscribeToUpdates() {
    // Clean up any existing subscription
    if (subscription.value) {
      subscription.value.unsubscribe()
    }

    subscription.value = consumer.subscriptions.create(
      { channel: 'WebsiteImportChannel', website_id: websiteId },
      {
        connected() {
          console.log('[Import] Connected to WebsiteImportChannel')
        },
        disconnected() {
          console.log('[Import] Disconnected from WebsiteImportChannel')
        },
        received(data) {
          handleUpdate(data)
        }
      }
    )
  }

  function handleUpdate(data) {
    console.log('[Import] Received update:', data)

    switch (data.event) {
      case 'progress':
        status.value = data.status
        progress.value = data.progress_percentage
        currentStep.value = data.current_step
        break

      case 'complete':
        status.value = 'completed'
        progress.value = 100
        currentStep.value = null
        normalizedData.value = data.normalized_data
        console.log('[Import] Normalized data received:', data.normalized_data)
        console.log('[Import] Website fields:', data.normalized_data?.website_fields)
        console.log('[Import] Images:', data.normalized_data?.images)
        console.log('[Import] Logo:', data.normalized_data?.images?.logo)
        // Fetch screenshots
        fetchScreenshots()
        break

      case 'error':
        status.value = 'failed'
        error.value = data.error
        currentStep.value = null
        break
    }
  }

  async function fetchScreenshots() {
    if (!importId.value) return

    try {
      const response = await api.getImport(websiteId, importId.value)
      screenshots.value = {
        desktop: response.data.desktop_screenshot_url,
        mobile: response.data.mobile_screenshot_url
      }
    } catch (err) {
      console.warn('[Import] Failed to fetch screenshots:', err)
    }
  }

  async function applyImport(editedData = null) {
    if (!importId.value) {
      error.value = 'No import to apply'
      return
    }

    try {
      // Pass edited data to the API so user changes are applied
      const response = await api.applyImport(websiteId, importId.value, editedData)
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to apply import'
      throw err
    }
  }

  async function pollStatus() {
    if (!importId.value || !isImporting.value) return

    try {
      const response = await api.getImport(websiteId, importId.value)
      const data = response.data

      status.value = data.status
      progress.value = data.progress_percentage
      currentStep.value = data.current_step

      if (data.status === 'completed') {
        normalizedData.value = data.normalized_data
        screenshots.value = {
          desktop: data.desktop_screenshot_url,
          mobile: data.mobile_screenshot_url
        }
      } else if (data.status === 'failed') {
        error.value = data.error_message
      }
    } catch (err) {
      console.warn('[Import] Failed to poll status:', err)
    }
  }

  function reset() {
    importId.value = null
    status.value = 'idle'
    progress.value = 0
    currentStep.value = null
    normalizedData.value = null
    error.value = null
    screenshots.value = { desktop: null, mobile: null }
  }

  function cleanup() {
    if (subscription.value) {
      subscription.value.unsubscribe()
      subscription.value = null
    }
  }

  return {
    // State
    importId,
    status,
    progress,
    currentStep,
    normalizedData,
    error,
    screenshots,

    // Computed
    isImporting,
    isComplete,
    hasFailed,
    stepLabel,

    // Actions
    startImport,
    applyImport,
    pollStatus,
    reset,
    cleanup
  }
}
